import type { PageMeta } from "@somasi/shared";
import { awsConfig } from "./config";
import { getAccessToken } from "./auth";

export interface ApiEnvelope<T> {
  data: T | null;
  error: { message: string; code?: string; details?: string } | null;
  count?: number | null;
  /** Where this page sits in the matching set; list responses only. */
  page?: PageMeta | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Authenticated call to the REST API. Throws on transport or HTTP failure. */
export async function apiFetch<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();

  // Every route sits behind the Cognito authorizer, so a call with no token can
  // only come back 401 — and that 401 is built by API Gateway, which the browser
  // may surface as an opaque network failure. Failing here says what actually
  // happened instead.
  if (!token) {
    throw new ApiError("Your session has expired — please sign in again", 401, "no_session");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const response = await fetch(`${awsConfig.apiUrl}${path}`, {
    method: init.method ?? "GET",
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const text = await response.text();
  let payload: ApiEnvelope<T>;
  try {
    payload = text ? (JSON.parse(text) as ApiEnvelope<T>) : { data: null, error: null };
  } catch {
    throw new ApiError(
      `The API returned a response that could not be read (${response.status})`,
      response.status,
    );
  }

  if (!response.ok) {
    throw new ApiError(
      payload.error?.message ?? `Request failed with status ${response.status}`,
      response.status,
      payload.error?.code,
    );
  }

  return payload;
}
